import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["overlay", "modal", "nearMeButton", "nearMeText", "nearMeInput", "nearMeLocation", "radiusSection", "latitude", "longitude"]

  connect() {
    this.boundHandleEscape = this.handleEscape.bind(this)
    document.addEventListener('keydown', this.boundHandleEscape)
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundHandleEscape)
  }

  open(event) {
    event?.preventDefault()
    
    if (!this.hasOverlayTarget || !this.hasModalTarget) return

    this.overlayTarget.classList.remove('hidden')
    this.overlayTarget.classList.add('flex')
    // Trigger animation
    requestAnimationFrame(() => {
      this.overlayTarget.classList.add('opacity-100')
      this.modalTarget.classList.remove('scale-95', 'opacity-0')
      this.modalTarget.classList.add('scale-100', 'opacity-100')
    })
    document.body.style.overflow = 'hidden'
  }

  close() {
    if (!this.hasOverlayTarget || !this.hasModalTarget) return

    this.overlayTarget.classList.remove('opacity-100')
    this.modalTarget.classList.remove('scale-100', 'opacity-100')
    this.modalTarget.classList.add('scale-95', 'opacity-0')
    
    setTimeout(() => {
      this.overlayTarget.classList.add('hidden')
      this.overlayTarget.classList.remove('flex')
      document.body.style.overflow = ''
    }, 200)
  }

  handleEscape(event) {
    if (event.key === 'Escape') {
      this.close()
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  toggleNearMe(event) {
    event.preventDefault()
    
    const isActive = this.nearMeInputTarget.value === 'true'
    
    if (isActive) {
      // Turn off Near Me
      this.nearMeInputTarget.value = ''
      this.nearMeLocationTarget.value = ''
      this.nearMeButtonTarget.classList.remove('border-primary', 'bg-primary/10', 'text-primary')
      this.nearMeButtonTarget.classList.add('border-gray-200', 'text-gray-600')
      this.nearMeTextTarget.textContent = this.nearMeButtonTarget.dataset.labelOff || 'Near Me'
      this.nearMeButtonTarget.querySelector('.fa-check')?.remove()
      this.radiusSectionTarget.classList.add('hidden')
      if (this.hasLatitudeTarget) this.latitudeTarget.value = ''
      if (this.hasLongitudeTarget) this.longitudeTarget.value = ''
    } else {
      // Get user's location
      this.nearMeTextTarget.textContent = 'Locating...'
      
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => this.handleGeolocationSuccess(position),
          (error) => this.handleGeolocationError(error),
          { enableHighAccuracy: true, timeout: 10000 }
        )
      } else {
        this.nearMeTextTarget.textContent = 'Location not supported'
      }
    }
  }

  async handleGeolocationSuccess(position) {
    const { latitude, longitude } = position.coords
    
    // Store coordinates
    if (this.hasLatitudeTarget) this.latitudeTarget.value = latitude
    if (this.hasLongitudeTarget) this.longitudeTarget.value = longitude
    this.nearMeInputTarget.value = 'true'
    
    // Update UI
    this.nearMeButtonTarget.classList.add('border-primary', 'bg-primary/10', 'text-primary')
    this.nearMeButtonTarget.classList.remove('border-gray-200', 'text-gray-600')
    this.radiusSectionTarget.classList.remove('hidden')
    
    // Add check icon if not present
    if (!this.nearMeButtonTarget.querySelector('.fa-check')) {
      const checkIcon = document.createElement('i')
      checkIcon.className = 'fas fa-check ml-auto'
      this.nearMeButtonTarget.appendChild(checkIcon)
    }
    
    // Reverse geocode to get location name
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
        { headers: { 'Accept-Language': document.documentElement.lang || 'en' } }
      )
      const data = await response.json()
      const locationName = data.address?.city || data.address?.town || data.address?.village || data.address?.municipality || 'Your Location'
      this.nearMeTextTarget.textContent = locationName
      this.nearMeLocationTarget.value = locationName
    } catch (error) {
      this.nearMeTextTarget.textContent = 'Your Location'
      this.nearMeLocationTarget.value = 'Your Location'
    }
  }

  handleGeolocationError(error) {
    console.error('Geolocation error:', error)
    this.nearMeTextTarget.textContent = 'Location unavailable'
    setTimeout(() => {
      this.nearMeTextTarget.textContent = this.nearMeButtonTarget.dataset.labelOff || 'Near Me'
    }, 2000)
  }
}
